import React from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Stack,
  Paper,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Work as WorkIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  WorkspacePremium as PremiumIcon,
  Link as LinkIcon,
  ReceiptLong as ReceiptIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
 
import { useNavigate } from 'react-router-dom';
import CommunityGroupsCard from '../components/CommunityGroupsCard';
import { useEffect, useState } from 'react';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import QuickActionsGrid from '../components/dashboard/QuickActionsGrid';

const CandidateDashboard = ({ jobSeekerId, accountStatus, skillsVerified }) => {
  const navigate = useNavigate();
  const isSuspended = accountStatus === 'suspended';
  
  // Feature toggle: hide Skilled Network card for now
  const SHOW_SKILLED_NETWORK = false;

  const handleBrowseJobs = () => {
    navigate('/jobs');
  };

  const handleViewProfile = () => {
    navigate('/jobseeker/profile-management');
  };

  const handleMyApplications = () => {
    navigate('/jobseeker/my-applications');
  };

  const handleViewPublicProfile = () => {
    if (jobSeekerId) {
      navigate(`/jobseeker/public/${jobSeekerId}`);
    } else {
       console.warn('Job Seeker ID not found');
    }
  };

  const quickActions = [
    {
      key: 'browse-jobs',
      title: 'Browse Jobs',
      description: 'Hand-picked roles aligned with your skills',
      icon: WorkIcon,
      accent: 'primary.main',
      actionLabel: 'Find roles',
      action: handleBrowseJobs
    },
    {
      key: 'community-groups',
      component: CommunityGroupsCard,
    },
    {
      key: 'update-profile',
      title: 'Update Profile',
      description: 'Keep your experience and availability current',
      icon: PersonIcon,
      accent: 'secondary.main',
      actionLabel: 'Edit profile',
      action: handleViewProfile,
      renderDetails: ({ scoreLoading, score, navigate: goTo }) => (
        <Box sx={{ mt: 1 }}>
          {scoreLoading ? (
            <Typography variant="caption" color="text.secondary">Loading score…</Typography>
          ) : score !== null ? (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Typography variant="caption" color="text.secondary">Current score:</Typography>
              <Typography variant="subtitle2" fontWeight={700}>{score}/10</Typography>
              {score < 10 && (
                <Button size="small" onClick={(e) => { e.stopPropagation(); goTo('/jobseeker/roadmap'); }}>See what's missing</Button>
              )}
            </Stack>
          ) : null}
        </Box>
      )
    },
    {
      key: 'invoices',
      title: 'Invoices',
      description: 'Create and manage your invoices',
      icon: ReceiptIcon,
      accent: 'warning.main',
      iconColor: 'grey.900',
      actionLabel: 'Manage invoices',
      action: () => navigate('/invoice')
    },
    {
      key: 'contact-support',
      title: 'Contact Support',
      description: 'Open a support case or view your existing cases.',
      icon: SupportIcon,
      accent: 'secondary.main',
      actionLabel: 'Open Support',
      action: () => navigate('/support')
    },
    {
      key: 'track-applications',
      title: 'Track Applications',
      description: 'Monitor conversations and employer replies',
      icon: AssignmentIcon,
      accent: 'success.main',
      actionLabel: 'Open tracker',
      action: handleMyApplications
    },
    {
      key: 'skilled-network',
      when: ({ skillsVerified: isVerified, showSkilledNetwork }) => !isVerified || showSkilledNetwork,
      title: 'Skilled Network',
      description: 'Apply for verified worker status & access premium jobs.',
      icon: PremiumIcon,
      accent: 'primary.main',
      actionLabel: 'Apply Now',
      action: () => navigate('/jobseeker/skilled-network'),
      renderDetails: ({ skillsVerified: isVerified, showSkilledNetwork }) => (
        isVerified && showSkilledNetwork ? (
          <Stack spacing={1} sx={{ width: '100%', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              You're verified! Access the exclusive groups below.
            </Typography>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Button variant="outlined" size="small" startIcon={<LinkIcon />} href="https://chat.whatsapp.com/placeholder" target="_blank" rel="noopener">
                WhatsApp Group
              </Button>
              <Button variant="outlined" size="small" startIcon={<LinkIcon />} href="https://t.me/placeholder" target="_blank" rel="noopener">
                Telegram Group
              </Button>
            </Stack>
          </Stack>
        ) : null
      )
    },
    {
      key: 'public-page',
      title: 'My Public Page',
      description: 'See how your profile looks to employers',
      icon: VisibilityIcon,
      accent: 'info.main',
      actionLabel: 'View public profile',
      action: handleViewPublicProfile
    }
 
  ];

  const [score, setScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);

  useEffect(() => {
    const loadScore = async () => {
      if (!jobSeekerId) return;
      setScoreLoading(true);
        try {
          const resp = await new JobSeekerProfileService().getRoadmapByProfileId(jobSeekerId);
          setScore(resp.score ?? 0);
        } catch (err) {
        console.error('Failed to load profile score', err);
      } finally {
        setScoreLoading(false);
      }
    };
    loadScore();
  }, [jobSeekerId]);

  return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={{ xs: 3, md: 4 }}>
          {isSuspended && (
            <Alert severity="warning" variant="filled" sx={{ borderRadius: 2 }}>
              <AlertTitle>Account Suspended</AlertTitle>
              Your account has been suspended and some features are limited. You will not be able to apply for jobs. Please contact support for more information.
            </Alert>
          )}

          <Box textAlign="center">
            <Typography variant="h5" fontWeight={600}>
              Quick actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Jump straight to the tasks that move your search forward.
            </Typography>

            {/* Notifications toggle moved to Admin UI; removed from Candidate Dashboard */}
          </Box>

          <QuickActionsGrid
            cards={quickActions}
            context={{
              navigate,
              score,
              scoreLoading,
              skillsVerified,
              showSkilledNetwork: SHOW_SKILLED_NETWORK,
            }}
          />
        </Stack>
      </Container>
  );
};

export default CandidateDashboard;